import React from 'react';
import { connect } from 'react-redux';
import get from 'lodash.get';

import * as dashboardActions from '../../dashboard/actions';
import * as eventActions from '../store/eventActions';
import { withRouterV6 } from '../../../helpers/withRouterV6.jsx';
import type { AppDispatch, RootState } from '../../../store';
import EntityPerformancePage from '../../entity/performance/EntityPerformancePage';
import type {
  EntityDashboardConfiguration,
  EntityPerformanceDispatchProps,
  EntityPerformanceStateProps,
  EntityType,
  ListRequest,
  TimeBracketKey,
  SummaryRecordPath,
  SummaryDataRequest
} from '../../entity/performance/types';

type EventPerformanceStateProps = EntityPerformanceStateProps & {
  eventId: string;
};

type EventPerformanceProps = EventPerformanceStateProps & EntityPerformanceDispatchProps;

type EventPerformanceRouteParams = {
  eventId?: string;
};

type EventPerformanceOwnProps = {
  match?: { params?: EventPerformanceRouteParams };
  params?: EventPerformanceRouteParams;
};

const EventPerformance: React.FC<EventPerformanceProps> = ({ eventId, ...rest }) => (
  <EntityPerformancePage entityId={eventId} entityType="event" {...rest} />
);

const mapState = (
  state: RootState,
  ownProps: EventPerformanceOwnProps
): EventPerformanceStateProps => {
  const eventId = ownProps.match?.params?.eventId ?? ownProps.params?.eventId ?? '';

  return {
    eventId,
    inited: Boolean(state.dashboard?.inited),
    modelRecord: state.event?.record as EventPerformanceStateProps['modelRecord'],
    summaryRecord: state.dashboard?.summaryRecord as EventPerformanceStateProps['summaryRecord'],
    previousSummaryRecord: state.dashboard?.previousSummaryRecord as EventPerformanceStateProps['previousSummaryRecord'],
    summaryDataRecord: state.dashboard?.summaryDataRecord as EventPerformanceStateProps['summaryDataRecord'],
    summaryType: (state.dashboard?.summaryType ?? null) as EventPerformanceStateProps['summaryType'],
    timePeriod: state.dashboard?.timePeriod as EventPerformanceStateProps['timePeriod'],
    timeBracket: state.dashboard?.timeBracket as EventPerformanceStateProps['timeBracket'],
    reportByDateAndMetricType: state.dashboard?.reportByDateAndMetricType as EventPerformanceStateProps['reportByDateAndMetricType'],
    configurations: (state.dashboard?.configurations ?? null) as EventPerformanceStateProps['configurations'],
    isSystemAdmin: get(state.session, 'isSystemAdmin') as EventPerformanceStateProps['isSystemAdmin'],
    isEventAdmin: get(state.session, 'isEventAdmin') as EventPerformanceStateProps['isEventAdmin'],
    isEventOrganizerAdmin: get(state.session, 'isEventOrganizerAdmin') as EventPerformanceStateProps['isEventOrganizerAdmin'],
    topDonationsList: (get(state.dashboard, 'dashboardTopDonations.byPage[1]', []) ?? []) as EventPerformanceStateProps['topDonationsList'],
    topFundraisersList: (get(state.dashboard, 'dashboardTopFundraisers.byPage[1]', []) ?? []) as EventPerformanceStateProps['topFundraisersList']
  };
};

const mapDispatch = (dispatch: AppDispatch): EntityPerformanceDispatchProps => ({
  init: (configurations: EntityDashboardConfiguration) => {
    dispatch(dashboardActions.init(configurations));
  },
  getSummaryRecord: (
    id: string,
    type: EntityType,
    timeBracket: TimeBracketKey,
    dateFrom?: string | null,
    dateTo?: string | null,
    path?: SummaryRecordPath
  ) => {
    const normalizedDateFrom = (dateFrom ?? null) as string | null;
    const normalizedDateTo = (dateTo ?? null) as string | null;
    dispatch(
      dashboardActions.getSummaryRecord(
        id,
        type,
        timeBracket,
        normalizedDateFrom,
        normalizedDateTo,
        path
      )
    );
  },
  getSummaryDataRecord: (request: SummaryDataRequest) => {
    dispatch(dashboardActions.getSummaryDataRecord(request));
  },
  onValueChange: (path: string, value: unknown) => {
    dispatch(dashboardActions.onValueChange(path, value));
  },
  getRecord: (id: string) => {
    dispatch(eventActions.getRecord(id));
  },
  getTopDonationsListData: (listKey: string, id: string, request: ListRequest) => {
    dispatch(dashboardActions.getDonationListData(listKey, id, request));
  },
  getTopFundraisersListData: (listKey: string, id: string, request: ListRequest) => {
    dispatch(dashboardActions.getFundraiserListData(listKey, id, request));
  }
});

export default withRouterV6(connect(mapState, mapDispatch)(EventPerformance));
